import { Box3, ColorRepresentation, Group, Material, Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D, PlaneGeometry, ShadowMaterial, Texture } from "three";
import { clone } from "three/examples/jsm/utils/SkeletonUtils.js";
import { ICollidable } from "../../../../three/physics/ICollidable";
import { Physics } from "../../../../three/physics/Physics";
import { PhysicsLayers } from "../../../../PhysicsLayers";
import { GameResourcesPackage } from "../../resources/GameResourcesPackage";
import { Ticker } from "pixi.js";

export class Level extends Object3D implements ICollidable {
    private readonly ground: Mesh;
    private readonly skyMaterial: MeshBasicMaterial;
    private readonly housesMaterial: MeshBasicMaterial;
    private readonly fence: Mesh;
    private readonly decorations: Mesh;
    private readonly shadowPlane: Mesh;
    private readonly groundBoundingBox: Box3;

    public get collidesWithLayer(): number {
        return 1 << PhysicsLayers.Layer1;
    }

    public get boundingBox(): Box3 {
        return this.groundBoundingBox.copy(this.ground.geometry.boundingBox).applyMatrix4(this.matrixWorld);
    }

    public get groundObject(): Object3D {
        return this.ground;
    }

    public get backgroundObject(): Object3D {
        return this.decorations;
    }

    public constructor(gameResources: GameResourcesPackage, initialHousesRotation: number) {
        super();
        const levelGroup = clone(gameResources.assets_game_Level_glb.scene.children[0]) as Group;
        this.ground = levelGroup.children[0] as Mesh;
        this.skyMaterial = (levelGroup.children[2] as Mesh).material as MeshBasicMaterial;

        this.add(levelGroup);
        this.groundBoundingBox = new Box3();
        this.fence = clone(gameResources.assets_game_Level_glb.scene.children[1]) as Mesh;
        this.fence.castShadow = true;
        this.add(this.fence);

        this.decorations = levelGroup.children[1] as Mesh;
        // Config Decorations material to be transparent. Figure out how to do it in blender scene setup.
        this.housesMaterial = this.decorations.material as MeshBasicMaterial;
        this.housesMaterial.transparent = true;
        this.housesMaterial.map.offset.x = initialHousesRotation;
        this.shadowPlane = this.constructShadowPlane(0x4a1e73, 0.4);
        this.shadowPlane.position.setY(0.01);
        this.add(this.shadowPlane);
        Physics.add(this);
        Ticker.shared.add(this.onTick, this);
    }

    private constructShadowPlane(shadowColor: ColorRepresentation, shadowOpacity: number): Mesh {
        const geometry = new PlaneGeometry(20, 20);
        geometry.rotateX(- Math.PI / 2);

        const material = new ShadowMaterial({
            color: shadowColor,
            opacity: shadowOpacity
        });

        const plane = new Mesh(geometry, material);
        plane.receiveShadow = true;
        return plane;
    }

    public rotateHouses(from: number, to: number, duration: number = 1000): Promise<void> {
        return new Promise<void>((resolve) => {
            let elapsed = 0;

            const easeInBack = (t: number, s: number = 1.70158): number => {
                return t * t * ((s + 1) * t - s);
            };

            const update = (_dt: number) => {
                elapsed += Ticker.shared.deltaMS;

                const t = Math.min(elapsed / duration, 1);
                const easedT = easeInBack(t); // ✅ use eased progress
                const current = from + (to - from) * easedT;
                this.housesMaterial.map.offset.x = current;

                if (t >= 1) {
                    Ticker.shared.remove(update, this);
                    resolve();
                }
            };

            Ticker.shared.add(update, this);
        });
    }

    private onTick(_dt: number): void {
        this.skyMaterial.map.offset.x += 0.0001;
    }

    public destroy(): void {
        Ticker.shared.remove(this.onTick, this);
    }

}